/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import axios from "axios";
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosInstance,
} from "axios";
import FormData from "form-data";

import type { ApiRequestOptions } from "./ApiRequestOptions";
import type { OnCancel } from "./CancelablePromise";
import type { OpenAPIConfig } from "./OpenAPI";
import { getIsSignoutInProgress } from "@context/authentication-flows";
import {
  isAntiBotError,
  isTokenExpiredError,
  logAntiBot403,
  applyDevSimulator,
} from "./request-error-guards";
import {
  handleTokenRefreshAndRetry,
  handleAntiBotRetry,
  handleAntiBotChallenge,
} from "./request-retry";
import { CLEARANCE_HEADER, rememberClearance } from '@shared/utils/antibot-clearance-store';

/**
 * Opens the global anti-bot challenge overlay by reporting a 428 challenge to the
 * lazily-imported AntiBotContext; silently no-ops when the context is unavailable.
 *
 * Used only for the repeat-428 case below (a redeemed replay that still came
 * back CHALLENGE) -- the first attempt's overlay is opened by
 * handleAntiBotChallenge itself, which also awaits the solve.
 */
async function openChallengeOverlay(axiosError: AxiosError): Promise<void> {
  try {
    const { reportAntiBotError } = await import('@/context/AntiBotContext');
    const responseBody = axiosError.response?.data as Record<string, unknown> | undefined;
    const siteKey = responseBody?.challenge_site_key;
    reportAntiBotError(
      new Error('Challenge required'),
      428,
      typeof siteKey === 'string' ? siteKey : undefined,
      { terminal: true },
    );
  } catch {
    // AntiBotContext may not be available
  }
}

/**
 * Detects the legacy anti-bot challenge encoding, where a challenge was signalled
 * as a 500 carrying an `AntiBotChallengeRequired` title instead of a 428.
 *
 * @param error - The Axios error to inspect
 * @returns True when the error is a legacy 500-encoded challenge
 */
function isLegacyChallenge500(error: AxiosError): boolean {
  if (error.response?.status !== 500) return false;
  const body = error.response.data as Record<string, unknown> | undefined;
  return body?.title === 'AntiBotChallengeRequired';
}

/**
 * Determines whether an error requires an anti-bot challenge, covering both the
 * canonical 428 status and the legacy 500-encoded form.
 *
 * @param axiosError - The Axios error to inspect
 * @returns True when a challenge overlay should be shown
 */
function isChallengeRequired(axiosError: AxiosError): boolean {
  return axiosError.response?.status === 428 || isLegacyChallenge500(axiosError);
}

/**
 * Decides whether an anti-bot error is retryable, i.e. an anti-bot rejection that
 * arrived as a 400 or 403 (as opposed to a hard challenge requirement).
 *
 * @param axiosError - The Axios error to inspect
 * @returns True when the request should be retried with a fresh anti-bot token
 */
function isRetryableAntiBotError(axiosError: AxiosError<any>): boolean {
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;
  if (!isAntiBotError(data)) return false;
  return status === 400 || status === 403;
}

/**
 * Decides whether an expired-token error should trigger a token refresh, guarding
 * against refreshing while a sign-out is already in progress.
 *
 * @param axiosError - The Axios error to inspect
 * @returns True when a token refresh-and-retry should be attempted
 */
function shouldRefreshToken(axiosError: AxiosError<any>): boolean {
  return isTokenExpiredError(axiosError) && !getIsSignoutInProgress();
}

/**
 * Decides whether a retryable anti-bot error should trigger a retry, ensuring a
 * request is only re-attempted once with a fresh anti-bot token.
 *
 * @param axiosError - The Axios error to inspect
 * @param isAntiBotRetry - Whether the current attempt is already an anti-bot retry
 * @returns True when a first anti-bot retry should be performed
 */
function shouldRetryAntiBot(axiosError: AxiosError<any>, isAntiBotRetry: boolean): boolean {
  return isRetryableAntiBotError(axiosError) && !isAntiBotRetry;
}

interface RetryContext {
  config: OpenAPIConfig;
  options: ApiRequestOptions;
  url: string;
  body: any;
  formData: FormData | undefined;
  headers: Record<string, string>;
  onCancel: OnCancel;
  axiosClient: AxiosInstance;
}

/**
 * Selects and executes the appropriate retry strategy for an error: token
 * refresh-and-retry, anti-bot retry, or none.
 *
 * @param axiosError - The Axios error to resolve a retry for
 * @param isAntiBotRetry - Whether the current attempt is already an anti-bot retry
 * @param ctx - The request context needed to replay the request
 * @returns A promise for the retried response, or undefined when no retry applies
 */
function resolveRetry<T>(
  axiosError: AxiosError<T>,
  isAntiBotRetry: boolean,
  ctx: RetryContext,
): Promise<AxiosResponse<T>> | undefined {
  const { config, options, url, body, formData, headers, onCancel, axiosClient } = ctx;
  if (shouldRefreshToken(axiosError)) {
    return handleTokenRefreshAndRetry(config, options, url, body, formData, headers, onCancel, axiosClient);
  }
  if (isChallengeRequired(axiosError) && !isAntiBotRetry) {
    return handleAntiBotChallenge(axiosError, config, options, url, body, formData, headers, onCancel, axiosClient);
  }
  if (shouldRetryAntiBot(axiosError, isAntiBotRetry)) {
    return handleAntiBotRetry(config, options, url, body, formData, headers, onCancel, axiosClient);
  }
  return undefined;
}

/**
 * Central Axios error handler: logs anti-bot 403s, resolves a token refresh,
 * anti-bot retry, or challenge-redemption retry via {@link resolveRetry}, and
 * falls back to returning the original error response.
 *
 * A CHALLENGE (428) on an attempt that is *already* a redeemed replay (i.e.
 * the solved token still didn't satisfy the backend) skips resolveRetry --
 * D9 caps replay at exactly one attempt -- and reports the failure as terminal.
 * It must not offer another checkbox: nothing is awaiting a token at that
 * point, so the widget would take a solve, drop it, close the overlay, and
 * leave the user exactly where they were, having done work for nothing.
 *
 * @param error - The thrown error (rethrown when it carries no response)
 * @param config - OpenAPI configuration used to replay the request on retry
 * @param options - Request options from the generated service
 * @param url - Fully resolved request URL
 * @param body - Request body payload
 * @param formData - Optional FormData for multipart requests
 * @param headers - Request headers
 * @param onCancel - Cancellation callback
 * @param axiosClient - Axios instance to use for retries
 * @param isAntiBotRetry - Whether this invocation is already an anti-bot retry
 * @returns The retried response, the challenge response, or the original error response
 */
async function handleAxiosError<T>(
  error: unknown,
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance,
  isAntiBotRetry = false,
): Promise<AxiosResponse<T>> {
  const axiosError = error as AxiosError<T>;
  if (!axiosError.response) throw error;

  maybeLogAntiBot403(axiosError, url, options, headers);

  const ctx: RetryContext = { config, options, url, body, formData, headers, onCancel, axiosClient };
  const retry = resolveRetry(axiosError, isAntiBotRetry, ctx);
  if (retry) return retry;

  if (isChallengeRequired(axiosError)) {
    await openChallengeOverlay(axiosError);
  }

  return axiosError.response;
}

/**
 * Logs the anti-bot 403 diagnostic when the error is actually an anti-bot rejection.
 */
function maybeLogAntiBot403<T>(
  axiosError: AxiosError<T>,
  url: string,
  options: ApiRequestOptions,
  headers: Record<string, string>,
): void {
  if (axiosError.response?.status === 403 && isAntiBotError(axiosError.response.data)) {
    logAntiBot403(axiosError, url, options, headers);
  }
}

/**
 * Sends an HTTP request via Axios, applying dev simulator hooks when active.
 *
 * @param config - OpenAPI configuration
 * @param options - Request options from the generated service
 * @param url - Fully resolved request URL
 * @param body - Request body payload
 * @param formData - Optional FormData for multipart requests
 * @param headers - Request headers
 * @param onCancel - Cancellation callback
 * @param axiosClient - Axios instance to use
 * @returns The Axios response
 */
export const sendRequest = async <T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance,
  isAntiBotRetry = false,
): Promise<AxiosResponse<T>> => {
  const simulated = await applyDevSimulator<T>();
  if (simulated) return simulated;

  const source = axios.CancelToken.source();

  const requestConfig: AxiosRequestConfig = {
    url,
    headers,
    data: body ?? formData,
    method: options.method,
    withCredentials: config.WITH_CREDENTIALS,
    cancelToken: source.token,
  };

  onCancel(() => source.cancel("The user aborted a request."));

  try {
    const response = await axiosClient.request(requestConfig);
    // A redeemed challenge answers with a clearance: keeping it is what stops the
    // next mutating call in the same flow from asking the same person again.
    rememberClearance(response.headers?.[CLEARANCE_HEADER.toLowerCase()]);
    return response;
  } catch (error) {
    return handleAxiosError(
      error,
      config,
      options,
      url,
      body,
      formData,
      headers,
      onCancel,
      axiosClient,
      isAntiBotRetry
    );
  }
};
